import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type SetPreferredTabKey = (key: string | undefined) => void;

const NOOP_SET_PREFERRED_TAB_KEY: SetPreferredTabKey = () => {
	// Intentional no-op: safe default/reset used as the context fallback and the
	// module-level setter when no provider is mounted. Setting a tab preference
	// without a mounted provider should silently do nothing rather than throw.
};

/**
 * Shape of the RovoLinkPicker tab-preference context.
 *
 * `preferredTabKey` is consumed by `RovoLinkPickerAdapter` so the link picker
 * can auto-select a particular tab the first time tabs load. It is meant to
 * be a one-shot signal: callers that set it should rely on the adapter to
 * clear it once the picker has been used (submit/cancel).
 */
interface RovoLinkPickerTabPreferenceContextValue {
	preferredTabKey: string | undefined;
	setPreferredTabKey: SetPreferredTabKey;
}

const RovoLinkPickerTabPreferenceContext = createContext<RovoLinkPickerTabPreferenceContextValue>({
	preferredTabKey: undefined,
	setPreferredTabKey: NOOP_SET_PREFERRED_TAB_KEY,
});

/**
 * Module-level holder for the latest setter from the most recently mounted
 * provider. This exists so non-React callers (specifically quick-insert action
 * closures inside `ExtensionManifest`s, which run outside the render tree)
 * can read the up-to-date setter without re-creating the manifest on every
 * render.
 *
 * The provider keeps this in sync via `useEffect`; consumers must read the
 * setter at action-execution time via `getSetPreferredTabKey()`.
 */
let latestSetPreferredTabKey: SetPreferredTabKey = NOOP_SET_PREFERRED_TAB_KEY;

/**
 * Returns the most recently registered `setPreferredTabKey`. Safe to call from
 * outside the React render tree (e.g. inside a quick-insert `action` closure).
 * Returns a no-op when no provider is mounted.
 */
export const getSetPreferredTabKey = (): SetPreferredTabKey => latestSetPreferredTabKey;

interface RovoLinkPickerTabPreferenceProviderProps {
	children: React.ReactNode;
}

export const RovoLinkPickerTabPreferenceProvider = ({
	children,
}: RovoLinkPickerTabPreferenceProviderProps): React.JSX.Element => {
	const [preferredTabKey, setPreferredTabKeyState] = useState<string | undefined>(undefined);

	const setPreferredTabKey = useCallback<SetPreferredTabKey>((key) => {
		setPreferredTabKeyState(key);
	}, []);

	useEffect(() => {
		latestSetPreferredTabKey = setPreferredTabKey;
		return () => {
			if (latestSetPreferredTabKey === setPreferredTabKey) {
				latestSetPreferredTabKey = NOOP_SET_PREFERRED_TAB_KEY;
			}
		};
	}, [setPreferredTabKey]);

	const value = useMemo<RovoLinkPickerTabPreferenceContextValue>(
		() => ({ preferredTabKey, setPreferredTabKey }),
		[preferredTabKey, setPreferredTabKey],
	);

	return (
		<RovoLinkPickerTabPreferenceContext.Provider value={value}>
			{children}
		</RovoLinkPickerTabPreferenceContext.Provider>
	);
};

export const useRovoLinkPickerTabPreference = (): RovoLinkPickerTabPreferenceContextValue =>
	useContext(RovoLinkPickerTabPreferenceContext);
